import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Flame, Clock, TrendingUp, Loader2, Sparkles } from "lucide-react";
import DealRow from "@/components/public/DealRow";
import { HotDealsWidget, CategoriesWidget, PromoCard, CommunityWidget } from "@/components/public/SidebarWidgets";
import { fetchPublicDeals } from "@/lib/api";
import { ASSETS, catMeta } from "@/config/categories";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "hot",      label: "Heißeste", icon: Flame },
  { key: "popular",  label: "Beliebt",  icon: TrendingUp },
  { key: "new",      label: "Neueste",  icon: Clock },
];

export default function Home() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const q = sp.get("q") || "";
  const category = slug ? decodeURIComponent(slug) : null;

  const [tab, setTab] = useState("hot");
  const [deals, setDeals] = useState([]);
  const [hotList, setHotList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const sortParam = tab === "new" ? "new" : "hot";
    fetchPublicDeals({ category: category || undefined, q: q || undefined, sort: sortParam, limit: 100 })
      .then((d) => {
        if (tab === "popular") d = [...d].sort((a, b) => (b.hot_votes || 0) - (a.hot_votes || 0));
        setDeals(d);
      })
      .finally(() => setLoading(false));
  }, [category, q, tab]);

  useEffect(() => {
    fetchPublicDeals({ sort: "hot" }).then(setHotList).catch(() => {});
  }, []);

  const isLanding = !category && !q;
  const meta = category ? catMeta(category) : null;
  const hottest = hotList[0]?.temperature || 0;

  return (
    <div data-testid="home-page" className="mx-auto max-w-6xl px-4 sm:px-6">
      {/* Hero */}
      {isLanding ? (
        <section
          className="relative overflow-hidden bg-slate-900 text-white rounded-3xl mt-6 p-8 md:p-10 shadow-2xl"
          data-testid="hero-band"
        >
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(620px circle at 82% 18%, rgba(255,138,61,0.38), transparent 60%), radial-gradient(560px circle at 96% 92%, rgba(255,98,165,0.42), transparent 60%), radial-gradient(560px circle at 58% 60%, rgba(162,89,255,0.40), transparent 62%)",
            }}
          />
          <img src="/wizard-icon.png" alt="" className="absolute right-6 sm:right-12 top-1/2 -translate-y-1/2 h-28 sm:h-40 w-28 sm:w-40 object-cover rounded-[28px] shadow-2xl rotate-6 hidden sm:block ring-4 ring-white/10" />
          <div className="relative max-w-xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-xs font-bold uppercase tracking-wider">
              <Sparkles className="h-3.5 w-3.5" /> Community Deals
            </span>
            <h1 className="font-outfit font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4 leading-[1.05]">
              Die <span className="ai-gradient-text">magischsten</span><br />Deals des Tages
            </h1>
            <p className="text-slate-300 mt-3 text-base max-w-md">
              Echte Schnäppchen, von der Community heiß abgestimmt. Finde, bewerte & teile die besten Deals. ✨
            </p>
            <div className="flex gap-3 mt-6">
              <div className="bg-white/10 backdrop-blur rounded-2xl px-4 py-3">
                <p className="font-outfit font-black text-2xl">{hotList.length}</p>
                <p className="text-xs text-slate-300">Live Deals</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-2xl px-4 py-3">
                <p className="font-outfit font-black text-2xl text-[#FF8A3D]">{hottest}°</p>
                <p className="text-xs text-slate-300">Heißester Deal</p>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="mt-6 flex items-center gap-4" data-testid="category-hero">
          {meta && (
            <div className="h-14 w-14 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${meta.color}15` }}>
              <meta.icon className="h-7 w-7" style={{ color: meta.color }} />
            </div>
          )}
          <div>
            <h1 className="font-outfit font-black text-3xl tracking-tight text-slate-900">{q ? `Suche: „${q}"` : category}</h1>
            <p className="text-slate-500 text-sm">{deals.length} Deals gefunden</p>
          </div>
        </section>
      )}

      {/* Body: feed + sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8 pb-4">
        <div className="lg:col-span-8 space-y-5">
          {/* Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="filter-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold font-outfit whitespace-nowrap inline-flex items-center gap-1.5 transition-all active:scale-95 ${active ? "bg-slate-900 text-white shadow-md" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                  data-testid={`tab-${t.key}`}
                >
                  <Icon className="h-4 w-4" /> {t.label}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="flex justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : deals.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center p-12 bg-slate-50 rounded-2xl border border-dashed border-slate-300" data-testid="empty-state">
              <img src={ASSETS.mascot3d} alt="" className="h-32 mb-4" />
              <p className="font-outfit font-bold text-slate-900 text-lg">Keine Deals gefunden</p>
              <p className="text-sm text-slate-500 mt-1">Der Zauberer sucht schon nach neuen Schnäppchen…</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4" data-testid="deal-feed">
              {deals.map((deal, i) => <DealRow key={deal.id} deal={deal} index={i} />)}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-4 space-y-6">
          <HotDealsWidget deals={hotList} />
          <PromoCard />
          <CategoriesWidget />
          {!user && <CommunityWidget totalDeals={hotList.length} />}
        </aside>
      </div>
    </div>
  );
}
